import type {
  CentralLead,
  CentralLeadDetailSection,
  CentralLeadDisplayField,
  CentralLeadInsightCard,
  CentralLeadViewModel,
} from "./types.ts";

const TECHNICAL_KEY_RE = /^(fbp|fbc|gclid|status_utm|fingerprint_|debug|user_agent|ip_address|geo_lat|geo_lng)$/i;
const SENSITIVE_KEY_RE = /(cpf|cnpj|documento|documentos|endereco|enderecos|logradouro|cep|telefone|telefones|email_extra|emails|vizinho|vizinhos|parente|parentes|compra|compras|beneficio|beneficios|vacina|imuno|tituloeleitor|registrogeral|dossier_raw|dadosbasicos|listadocumentos|pep|conjuge|nascimento|nome_completo|nome_mae|nomemae|nome_pai|nomepai)/i;

const SUMMARY_KEYS = [
  "product_interest",
  "product_slug",
  "source_marketing",
  "versao",
  "geo_city",
  "geo_region",
  "geo_country",
  "dispositivo",
  "device_brand",
  "navegador",
  "tempo_sessao",
  "cliques_cta",
  "secoes_visualizadas",
  "profundidade_scroll",
];

const QUALIFICATION_KEYS = [
  "empresa",
  "cargo",
  "estado",
  "cidade",
  "pesquisa_genero",
  "pesquisa_faixa_etaria",
  "pesquisa_profissao",
  "pesquisa_renda",
  "tem_curso_avaliacao",
  "tem_empresa_aberta",
  "grupo",
];

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function textValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Sim" : "Nao";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "string") return value.trim();
  return "";
}

function compactValue(value: unknown): string {
  const direct = textValue(value);
  if (direct) return direct;
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`;
  const obj = objectValue(value);
  const keys = Object.keys(obj);
  return keys.length ? `${keys.length} campo${keys.length === 1 ? "" : "s"}` : "";
}

function formatDateValue(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function formatFieldValue(key: string, value: unknown): string {
  const formatted = compactValue(value);
  if (!formatted) return "";
  if (/(captured|data|date|created|updated|nascimento)$/i.test(key)) return formatDateValue(formatted);
  return formatted;
}

function pathValue(root: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => objectValue(current)[key], root);
}

export function humanizeCustomFieldKey(key: string): string {
  return key
    .replace(/^pesquisa_/, "")
    .replace(/^geo_/, "")
    .replace(/^device_/, "device ")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function addField(fields: CentralLeadDisplayField[], key: string, label: string, value: unknown): void {
  const formatted = formatFieldValue(key, value);
  if (!formatted) return;
  fields.push({ key, label, value: formatted });
}

function scoreTone(risk: unknown): CentralLeadInsightCard["tone"] {
  const value = textValue(risk).toLowerCase();
  if (value.includes("baixo")) return "good";
  if (value.includes("alto")) return "warn";
  return "neutral";
}

function buildInsightCards(customFields: Record<string, unknown>): CentralLeadInsightCard[] {
  const parsed = objectValue(pathValue(customFields, "enrichment.dossier_parsed"));
  const cards: CentralLeadInsightCard[] = [];
  const csb = textValue(parsed.score_csb);
  const csbRisk = textValue(parsed.score_csb_risco);
  const csba = textValue(parsed.score_csba);
  const csbaRisk = textValue(parsed.score_csba_risco);

  if (csb) cards.push({ key: "score_csb", label: "Score CSB", value: csb, detail: csbRisk, tone: scoreTone(csbRisk) });
  if (csba) cards.push({ key: "score_csba", label: "Score CSBA", value: csba, detail: csbaRisk, tone: scoreTone(csbaRisk) });
  addInsight(cards, "profissao", "Profissao", parsed.profissao_desc, parsed.profissao_cbo);
  addInsight(cards, "escolaridade", "Escolaridade", parsed.escolaridade);
  addInsight(cards, "renda", "Renda", parsed.faixa_renda || parsed.renda, parsed.poder_aquisitivo);
  addInsight(cards, "mosaic", "Perfil Mosaic", parsed.mosaic_classe, parsed.mosaic_desc);
  addInsight(cards, "local", "Local", [parsed.endereco_cidade, parsed.endereco_uf].filter(Boolean).join(" / "));

  const enrichment = objectValue(customFields.enrichment);
  addInsight(
    cards,
    "enrichment_status",
    "Enriquecimento",
    enrichment.status || customFields.enrichment_status,
    enrichment.match_score ? `match ${enrichment.match_score}` : enrichment.match_method,
  );

  return cards;
}

function addInsight(
  cards: CentralLeadInsightCard[],
  key: string,
  label: string,
  value: unknown,
  detail?: unknown,
): void {
  const formatted = textValue(value);
  if (!formatted) return;
  cards.push({ key, label, value: formatted, detail: textValue(detail) });
}

function isDisplayableScalar(value: unknown): boolean {
  return ["string", "number", "boolean"].includes(typeof value) && !!compactValue(value);
}

function isSensitivePath(path: string): boolean {
  return SENSITIVE_KEY_RE.test(path);
}

function isTechnicalKey(key: string): boolean {
  return TECHNICAL_KEY_RE.test(key) || key.startsWith("url_") || key === "captured_at_client";
}

function countHiddenSensitive(value: unknown, path = ""): number {
  if (isSensitivePath(path)) return 1;
  if (Array.isArray(value)) {
    return value.reduce((total, item, index) => total + countHiddenSensitive(item, `${path}.${index}`), 0);
  }
  const obj = objectValue(value);
  return Object.entries(obj).reduce((total, [key, item]) => total + countHiddenSensitive(item, path ? `${path}.${key}` : key), 0);
}

function safeFlatFields(value: unknown, path = "", limit = 6): CentralLeadDisplayField[] {
  const fields: CentralLeadDisplayField[] = [];
  const visit = (current: unknown, currentPath: string) => {
    if (fields.length >= limit || isSensitivePath(currentPath)) return;
    if (isDisplayableScalar(current)) {
      fields.push({
        key: currentPath,
        label: humanizeCustomFieldKey(currentPath.split(".").pop() || currentPath),
        value: compactValue(current),
      });
      return;
    }
    if (Array.isArray(current)) {
      for (const [index, item] of current.slice(0, 2).entries()) visit(item, `${currentPath}.${index + 1}`);
      return;
    }
    for (const [key, item] of Object.entries(objectValue(current))) {
      if (isTechnicalKey(key)) continue;
      visit(item, currentPath ? `${currentPath}.${key}` : key);
    }
  };
  visit(value, path);
  return fields;
}

function buildDetailSections(customFields: Record<string, unknown>): CentralLeadDetailSection[] {
  const sections: CentralLeadDetailSection[] = [];
  for (const [key, value] of Object.entries(customFields)) {
    if (isTechnicalKey(key) || isSensitivePath(key) || QUALIFICATION_KEYS.includes(key) || SUMMARY_KEYS.includes(key)) continue;
    if (key === "enrichment") continue;
    if (isDisplayableScalar(value)) continue;

    const fields = safeFlatFields(value, key, 8);
    if (!fields.length) continue;
    sections.push({
      key,
      title: humanizeCustomFieldKey(key),
      count: Array.isArray(value) ? value.length : Object.keys(objectValue(value)).length,
      fields,
    });
  }
  return sections;
}

export function buildCentralLeadView(lead: CentralLead): CentralLeadViewModel {
  const customFields = objectValue(lead.customFields);
  const summaryFields: CentralLeadDisplayField[] = [];
  addField(summaryFields, "email", "Email", lead.email);
  addField(summaryFields, "source", "Origem", lead.source);
  addField(summaryFields, "capturedAt", "Capturado", lead.capturedAt);
  for (const key of SUMMARY_KEYS) addField(summaryFields, key, humanizeCustomFieldKey(key), customFields[key]);

  const qualificationFields: CentralLeadDisplayField[] = [];
  for (const key of QUALIFICATION_KEYS) addField(qualificationFields, key, humanizeCustomFieldKey(key), customFields[key]);

  const utm = objectValue(lead.utm);
  for (const key of ["source", "medium", "campaign", "term", "content"]) {
    addField(qualificationFields, `utm.${key}`, `UTM ${humanizeCustomFieldKey(key)}`, utm[key]);
  }

  return {
    summaryFields: uniqueFields(summaryFields).slice(0, 10),
    insightCards: buildInsightCards(customFields),
    qualificationFields: uniqueFields(qualificationFields),
    detailSections: buildDetailSections(customFields),
    hiddenSensitiveCount: countHiddenSensitive(customFields),
  };
}

function uniqueFields(fields: CentralLeadDisplayField[]): CentralLeadDisplayField[] {
  const seen = new Set<string>();
  return fields.filter((field) => {
    const key = `${field.label}:${field.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
